// Tenantra Inc. - "Page two" of the tenant panel: the full screening application on file,
// joined live from the screening record so it is always retrievable for record keeping.
import { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { Section, Field, fmtDate } from './DetailPanel';

const Val = ({ children }) => <span className="text-slate-800">{children || 'Not provided'}</span>;

const DOC = { paystub: 'Pay stub', bank_statement: 'Bank statement', savings_statement: 'Savings statement', equifax_report: 'Equifax report', id: 'Government ID', reference: 'Reference letter' };

const PROVINCE = { ON: 'Ontario', BC: 'British Columbia', AB: 'Alberta', MB: 'Manitoba', SK: 'Saskatchewan', QC: 'Quebec', NB: 'New Brunswick', NS: 'Nova Scotia', PE: 'Prince Edward Island', NL: 'Newfoundland and Labrador', NT: 'Northwest Territories', YT: 'Yukon', NU: 'Nunavut' };

const EMP = { full_time: 'Full-time', part_time: 'Part-time', self_employed: 'Self-employed', student: 'Student', retired: 'Retired', unemployed: 'Unemployed' };

function Contact({ c }) {
  if (!c) return <p className="text-sm text-slate-500">Not provided</p>;
  return (
    <dl className="grid grid-cols-2 gap-4">
      <Field label="Name">{c.full_name || c.name}</Field>
      <Field label="Relationship">{c.relationship}</Field>
      <Field label="Phone">{c.phone}</Field>
      <Field label="Email">{c.email}</Field>
    </dl>
  );
}

function DocFiles({ appId, docs }) {
  const [busy, setBusy] = useState(null);
  const open = async (d, download) => {
    if (!appId) { toast.error('Documents are not available for this record.'); return; }
    setBusy(d.file_id + download);
    try {
      const r = await api.get(`/screening/applications/${appId}/documents/${d.file_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (download) { const a = document.createElement('a'); a.href = url; a.download = d.filename || d.doc_type || 'document'; a.click(); }
      else window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(null); }
  };
  return (
    <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="application-documents">
      {docs.map((d, i) => (
        <li key={i} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
          <span className="min-w-0">
            <span className="block text-slate-800 truncate">{DOC[d.doc_type] || d.name || d.type || `Document ${i + 1}`}</span>
            {d.filename && <span className="block text-[11px] text-slate-400 truncate">{d.filename}</span>}
          </span>
          {d.file_id && (
            <span className="flex items-center gap-3 shrink-0">
              <button onClick={() => open(d, false)} disabled={busy} className="text-teal-700 hover:underline disabled:opacity-50" data-testid={`application-doc-view-${d.file_id}`}>View</button>
              <button onClick={() => open(d, true)} disabled={busy} className="text-slate-500 hover:underline disabled:opacity-50" data-testid={`application-doc-download-${d.file_id}`}>Download</button>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function TenantApplication({ tenantId, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let active = true;
    setData(null); setErr('');
    api.get(`/tenants/${tenantId}/application`)
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) setErr(e.response?.status === 404 ? 'No screening application is on file for this tenant (added manually or imported).' : apiHelpers.handleError(e)); });
    return () => { active = false; };
  }, [tenantId]);

  const a = data;
  const addr = (x) => [x.street, x.unit && `Unit ${x.unit}`, x.city, PROVINCE[x.region] || x.region, x.postal_code].filter(Boolean).join(', ');
  const dur = (x) => [x.years && `${x.years} yr`, x.months && `${x.months} mo`].filter(Boolean).join(' ') || null;

  return (
    <div className="animate-in fade-in slide-in-from-right-2 duration-200">
      <button onClick={onBack} data-testid="application-back" className="w-full flex items-center gap-2 px-6 py-3 text-sm font-medium text-teal-700 hover:bg-slate-50 border-b border-slate-100">
        <ArrowLeft className="w-4 h-4" /> Back to summary
      </button>

      {err && <div className="px-6 py-8 text-sm text-slate-500" data-testid="application-empty">{err}</div>}
      {!a && !err && <div className="p-6 space-y-3">{[0, 1, 2, 3].map(i => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${80 - i * 12}%` }} />)}</div>}

      {a && (
        <div data-testid="tenant-application">
          <div className="px-6 pt-5 pb-1">
            <p className="text-[11px] text-slate-400">Screening application</p>
            <p className="text-sm font-semibold text-slate-900">{a.reference || 'On file'}</p>
            <p className="text-xs text-slate-500 mt-0.5">Submitted {fmtDate(a.submitted_at)}{a.status ? ` · ${a.status}` : ''}</p>
          </div>

          <Section title="Applicant">
            <dl className="grid grid-cols-2 gap-4">
              <Field label="Full name">{[a.applicant?.first_name, a.applicant?.middle_name, a.applicant?.last_name].filter(Boolean).join(' ') || 'Not provided'}</Field>
              <Field label="Date of birth">{fmtDate(a.applicant?.date_of_birth)}</Field>
              <Field label="Email">{a.applicant?.email}</Field>
              <Field label="Phone">{a.applicant?.phone}</Field>
              <Field label="Desired move-in" wide>{fmtDate(a.move_in_date)}</Field>
            </dl>
          </Section>

          {a.addresses?.length > 0 && (
            <Section title="Address history">
              <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                {a.addresses.map((x, i) => (
                  <li key={i} className="px-3 py-2 text-sm">
                    <p className="text-slate-800">{addr(x)}</p>
                    <p className="text-xs text-slate-500">{x.current ? 'Current' : 'Previous'}{dur(x) ? ` · ${dur(x)}` : ''}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {a.employment && (
            <Section title="Employment">
              <dl className="grid grid-cols-2 gap-4">
                <Field label="Status">{EMP[a.employment.status] || a.employment.status}</Field>
                <Field label="Employer">{a.employment.employer}</Field>
                <Field label="Job title">{a.employment.job_title}</Field>
                <Field label="Time there">{dur(a.employment) || 'Not provided'}</Field>
              </dl>
            </Section>
          )}

          <Section title="Personal reference"><Contact c={a.personal_reference} /></Section>
          <Section title="Emergency contact"><Contact c={a.emergency_contact} /></Section>

          {a.pets?.has_pets && (
            <Section title="Pets" aside={`${a.pets.list?.length || 0}`}>
              <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                {(a.pets.list || []).map((p, i) => (
                  <li key={i} className="px-3 py-2 text-sm text-slate-800">{[p.type, p.breed, p.name].filter(Boolean).join(' · ') || 'Pet'}</li>
                ))}
              </ul>
            </Section>
          )}

          {(a.household?.adults?.length > 0 || a.household?.minors?.length > 0) && (
            <Section title="Household" aside={a.household?.minors_count ? `${a.household.minors_count} under 18` : null}>
              <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                {(a.household.adults || []).map((h, i) => (
                  <li key={`ad-${i}`} className="px-3 py-2 flex justify-between text-sm"><span className="text-slate-800">{h.full_name}</span><span className="text-xs text-slate-500">{h.relationship || 'Adult'}</span></li>
                ))}
                {(a.household.minors || []).map((m, i) => (
                  <li key={`mi-${i}`} className="px-3 py-2 flex justify-between text-sm"><span className="text-slate-800">{m.full_name}</span><span className="text-xs text-slate-500">Minor · {fmtDate(m.date_of_birth)}</span></li>
                ))}
              </ul>
            </Section>
          )}

          {a.co_applicants?.length > 0 && (
            <Section title="Co-applicants" aside={`${a.co_applicants.length}`}>
              {a.co_applicants.map((co, i) => (
                <dl key={i} className="grid grid-cols-2 gap-4 border border-slate-100 rounded-md p-3 mb-2">
                  <Field label="Name">{[co.first_name, co.last_name].filter(Boolean).join(' ')}</Field>
                  <Field label="Relationship">{co.relationship}</Field>
                  <Field label="Email">{co.email}</Field>
                  <Field label="Phone">{co.phone}</Field>
                </dl>
              ))}
            </Section>
          )}

          {a.credit_check && (
            <Section title="Credit check">
              <dl className="grid grid-cols-2 gap-4">
                <Field label="Score">{a.credit_check.score ?? 'N/A'}</Field>
                <Field label="On-time payments">{a.credit_check.on_time_pct != null ? `${a.credit_check.on_time_pct}%` : 'N/A'}</Field>
                <Field label="Collections">{a.credit_check.collections ?? 'N/A'}</Field>
                <Field label="Bankruptcy">{a.credit_check.bankruptcy ? 'Yes' : 'No'}</Field>
                <Field label="Utilization">{a.credit_check.utilization_pct != null ? `${a.credit_check.utilization_pct}%` : 'N/A'}</Field>
                <Field label="History">{a.credit_check.history_months != null ? `${a.credit_check.history_months} mo` : 'N/A'}</Field>
              </dl>
            </Section>
          )}

          {a.fit && (
            <Section title="Fit assessment" aside={a.fit.band}>
              <p className="text-sm text-slate-800 mb-2"><span className="font-semibold">{a.fit.score}</span> / 100{a.fit.flags?.length ? ` · ${a.fit.flags.join(', ')}` : ''}</p>
              {a.fit.factors?.length > 0 && (
                <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                  {a.fit.factors.map((f, i) => (
                    <li key={i} className="px-3 py-2 flex justify-between gap-3 text-sm"><span className="text-slate-700">{f.label}</span><span className="text-xs text-slate-500">{f.points != null ? `${f.points}/${f.max}` : 'Pending'}</span></li>
                  ))}
                </ul>
              )}
            </Section>
          )}

          {a.documents?.length > 0 && (
            <Section title="Documents" aside={`${a.documents.length}`}>
              <DocFiles appId={a.app_id} docs={a.documents} />
            </Section>
          )}

          {a.consent && (
            <Section title="Consent">
              <p className="text-sm text-slate-700">Accepted{a.consent.version ? ` (${a.consent.version})` : ''}{a.consent.accepted_at ? ` on ${fmtDate(a.consent.accepted_at)}` : ''}.</p>
            </Section>
          )}

          {a.fee && (
            <Section title="Application fee">
              <p className="text-sm text-slate-800">${Number(a.fee.amount || 0).toFixed(2)} · <span className="text-slate-500">{a.fee.status || 'unknown'}</span></p>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
